import { useEffect, useRef, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import type { AccountUser, AuthValues, CatalogSnapshot, Wallpaper } from '../types';
import { api, setAccountContext } from '../lib/api';
import { wallpaperFromArtwork, type ArtworkRow } from '../lib/catalog';
import { passwordProblem } from '../lib/password';
import { supabase } from '../lib/supabase';

const GUEST_KEY = 'mujian-favorites';
const AUTH_EVENT = 'mujian-auth-event';
const FAVORITES_EVENT = 'mujian-favorites-event';

export function readGuestFavorites(): string[] {
  try {
    const values = JSON.parse(localStorage.getItem(GUEST_KEY) || '[]');
    return Array.isArray(values)
      ? [...new Set(values.filter((value): value is string => typeof value === 'string'))]
      : [];
  } catch {
    return [];
  }
}

function displayNameOf(user: User): string {
  const raw = user.user_metadata?.display_name;
  const name = typeof raw === 'string' ? Array.from(raw.trim()).slice(0, 24).join('').trim() : '';
  if (name) return name;
  const local =
    typeof user.email === 'string'
      ? Array.from(user.email.split('@')[0]?.trim() ?? '')
          .slice(0, 24)
          .join('')
      : '';
  return local || '幕间用户';
}

function accountUser(user: User): AccountUser {
  return {
    id: user.id,
    email: user.email ?? '',
    displayName: displayNameOf(user),
    createdAt: user.created_at,
    emailVerified: Boolean(user.email_confirmed_at ?? user.confirmed_at),
  };
}

function friendlyAuthError(error: { message?: string; status?: number } | null): string {
  const message = error?.message ?? '';
  if (/invalid login credentials/i.test(message)) return '邮箱或密码不正确。';
  if (/email not confirmed/i.test(message)) return '请先点击邮件中的确认链接完成验证。';
  if (/already registered|already been registered|user already exists/i.test(message))
    return '该邮箱已注册，请直接登录。';
  if (/rate limit|too many|only request this after/i.test(message))
    return '操作过于频繁，请稍后再试。';
  if (/password/i.test(message) && /(least|length)/i.test(message))
    return '密码需为 6–72 个字符，且不超过 72 字节。';
  return message || '操作未能完成，请稍后重试。';
}

async function currentSession() {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session;
}

/**
 * Published artworks become the storefront source. Until the first artwork is
 * published (or if the query fails) the legacy catalog keeps the site usable,
 * and legacy ownership is merged by artwork ID.
 */
async function publishedItems(
  fallback: Wallpaper[],
  owned: Map<string, boolean>,
): Promise<Wallpaper[]> {
  if (!supabase) return fallback;
  let rows: ArtworkRow[] = [];
  try {
    const { data, error } = await supabase
      .from('artworks')
      .select('id,title,amount_minor,currency,metadata')
      .eq('active', true)
      .abortSignal(AbortSignal.timeout(4000));
    if (!error && data) rows = data as ArtworkRow[];
  } catch {
    /* Fall back to the legacy catalog, but keep entitlement ownership. */
  }
  const remaining = new Map(rows.map((row) => [row.id, row]));
  const items = fallback.map((item) => {
    const ownedHere = item.owned || owned.get(item.id) === true;
    const row = remaining.get(item.id);
    if (!row) return ownedHere === item.owned ? item : { ...item, owned: true };
    remaining.delete(item.id);
    return wallpaperFromArtwork(row, ownedHere);
  });
  for (const row of remaining.values())
    items.push(wallpaperFromArtwork(row, owned.get(row.id) ?? false));
  return items;
}

export function useAccountData(notify: (message: string) => void) {
  const [state, setState] = useState<CatalogSnapshot>(() => ({
    items: [],
    paymentMode: 'demo',
    user: null,
    favorites: readGuestFavorites(),
  }));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  const [favoriteSyncing, setFavoriteSyncing] = useState(false);
  const [recovery, setRecovery] = useState(false);
  const current = useRef(state);
  const notifyRef = useRef(notify);
  notifyRef.current = notify;
  const epoch = useRef(0),
    fetchSerial = useRef(0),
    favoriteSerial = useRef(0);
  const remoteFavorites = useRef<string[]>([]);
  const busy = useRef(false);
  const channel = useRef<BroadcastChannel | null>(null);

  function apply(next: CatalogSnapshot) {
    const normalized = { ...next, favorites: next.user ? next.favorites : readGuestFavorites() };
    current.current = normalized;
    setState(normalized);
    // The legacy backend still uses a browser-bound guest cookie for checkout and
    // downloads, so every legacy mutation stays on the "guest" account context.
    setAccountContext(null);
    remoteFavorites.current = next.user ? [...next.favorites] : [];
  }

  function setFavorites(favorites: string[]) {
    const next = { ...current.current, favorites };
    current.current = next;
    setState(next);
  }

  function broadcast(kind: 'auth' | 'favorites') {
    const message = { kind, nonce: crypto.randomUUID() };
    try {
      localStorage.setItem(kind === 'auth' ? AUTH_EVENT : FAVORITES_EVENT, JSON.stringify(message));
    } catch {
      /* BroadcastChannel also works when storage is unavailable. */
    }
    channel.current?.postMessage(message);
  }

  async function snapshot(): Promise<CatalogSnapshot> {
    const legacy = await api<CatalogSnapshot>('/api/catalog');
    const owned = new Map(legacy.items.map((item) => [item.id, item.owned]));
    const session = await currentSession();
    const user = session ? accountUser(session.user) : null;
    let favorites: string[] = readGuestFavorites();
    if (user) {
      const [favoritesResult, entitlementsResult] = await Promise.all([
        supabase!
          .from('favorites')
          .select('artwork_id')
          .eq('user_id', user.id)
          .order('created_at', { ascending: true })
          .abortSignal(AbortSignal.timeout(8000)),
        supabase!
          .from('entitlements')
          .select('artwork_id')
          .is('revoked_at', null)
          .abortSignal(AbortSignal.timeout(8000)),
      ]);
      if (favoritesResult.error) throw new Error('收藏读取失败，请稍后重试。');
      favorites = (favoritesResult.data ?? []).map((row) => row.artwork_id as string);
      for (const row of entitlementsResult.data ?? []) owned.set(row.artwork_id as string, true);
    }
    const catalog = { ...legacy, items: await publishedItems(legacy.items, owned) };
    return user ? { ...catalog, user, favorites } : { ...catalog, user: null, favorites };
  }

  async function refresh(quiet = false) {
    const generation = epoch.current,
      serial = ++fetchSerial.current;
    if (!quiet) {
      setLoading(true);
      setError('');
    }
    try {
      const next = await snapshot();
      if (generation !== epoch.current || serial !== fetchSerial.current)
        return current.current.items;
      apply(next);
      setLoading(false);
      setError('');
      return next.items;
    } catch (refreshError) {
      if (serial === fetchSerial.current && !quiet)
        setError((refreshError as Error).message || '账号数据加载失败，请稍后重试。');
      return [];
    } finally {
      if (serial === fetchSerial.current) setLoading(false);
    }
  }
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    let lastNonce = '';
    function receive(message: { kind?: string; nonce?: string }) {
      if (message.nonce && message.nonce === lastNonce) return;
      lastNonce = message.nonce ?? '';
      if (busy.current) return;
      if (message.kind === 'auth') {
        void refreshRef.current(false);
      }
      if (message.kind === 'favorites') void refreshRef.current(true);
    }
    const onStorage = (event: StorageEvent) => {
      if (event.key === AUTH_EVENT || event.key === FAVORITES_EVENT) {
        try {
          receive(JSON.parse(event.newValue ?? '{}'));
        } catch {
          /* Ignore malformed notifications. */
        }
      } else if ((event.key === GUEST_KEY || event.key === null) && !current.current.user)
        setFavorites(readGuestFavorites());
    };
    const onFocus = () => {
      if (!busy.current && document.visibilityState === 'visible') void refreshRef.current(true);
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    if ('BroadcastChannel' in window) {
      channel.current = new BroadcastChannel('mujian-account');
      channel.current.onmessage = (event) => receive(event.data ?? {});
    }
    let unsubscribe = () => {};
    if (supabase) {
      const { data } = supabase.auth.onAuthStateChange((event) => {
        if (event === 'PASSWORD_RECOVERY') setRecovery(true);
        if (event === 'SIGNED_OUT') setRecovery(false);
        if (busy.current) return;
        if (['SIGNED_IN', 'SIGNED_OUT', 'USER_UPDATED', 'TOKEN_REFRESHED'].includes(event)) {
          void refreshRef.current(true);
        }
      });
      unsubscribe = () => data.subscription.unsubscribe();
    }
    void refreshRef.current(false);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      channel.current?.close();
      unsubscribe();
    };
  }, []);

  function saveFavorite(id: string, saved: boolean, position?: number) {
    if (busy.current) {
      notifyRef.current('正在更新登录状态，请稍等。');
      return;
    }
    const before = current.current;
    const desired = before.favorites.filter((value) => value !== id);
    if (saved)
      desired.splice(
        position === undefined ? desired.length : Math.max(0, Math.min(position, desired.length)),
        0,
        id,
      );
    setFavorites(desired);
    if (!before.user) {
      try {
        localStorage.setItem(GUEST_KEY, JSON.stringify(desired));
      } catch {
        notifyRef.current('浏览器限制了存储，收藏只保留在当前页面。');
      }
      return;
    }
    if (!supabase) return;
    const owner = before.user.id,
      serial = ++favoriteSerial.current;
    setFavoriteSyncing(true);
    void (async () => {
      try {
        const { error: favoriteError } = saved
          ? await supabase!
              .from('favorites')
              .upsert(
                { user_id: owner, artwork_id: id },
                { onConflict: 'user_id,artwork_id', ignoreDuplicates: true },
              )
          : await supabase!.from('favorites').delete().eq('user_id', owner).eq('artwork_id', id);
        if (favoriteError) throw new Error('收藏未能保存，请重试。');
        if (current.current.user?.id !== owner) return;
        remoteFavorites.current = desired;
        if (serial === favoriteSerial.current) setFavorites(desired);
        broadcast('favorites');
      } catch (favoriteError) {
        if (current.current.user?.id !== owner) return;
        if (serial === favoriteSerial.current) setFavorites([...remoteFavorites.current]);
        notifyRef.current((favoriteError as Error).message || '收藏未能保存，请重试。');
      } finally {
        if (current.current.user?.id === owner) setFavoriteSyncing(false);
      }
    })();
  }

  function toggleFavorite(id: string) {
    saveFavorite(id, !current.current.favorites.includes(id));
  }

  async function mergeGuestFavorites(userId: string) {
    const guest = readGuestFavorites().filter((id) =>
      current.current.items.some((item) => item.id === id),
    );
    if (!guest.length || !supabase) return 0;
    const { error: mergeError } = await supabase.from('favorites').upsert(
      guest.map((artworkId) => ({ user_id: userId, artwork_id: artworkId })),
      { onConflict: 'user_id,artwork_id', ignoreDuplicates: true },
    );
    if (mergeError) throw new Error('本机收藏未能合并，请稍后重试。');
    try {
      localStorage.removeItem(GUEST_KEY);
    } catch {
      /* Account favorites remain the source of truth. */
    }
    return guest.length;
  }

  async function mergeFavoritesQuietly(userId: string) {
    try {
      return await mergeGuestFavorites(userId);
    } catch (mergeError) {
      // A failed merge must never hide an otherwise valid sign-in; guest
      // favorites stay in localStorage and are retried on the next login.
      notifyRef.current((mergeError as Error).message);
      return 0;
    }
  }

  async function authenticate(kind: 'login' | 'register', values: AuthValues) {
    if (!supabase) throw new Error('Supabase 尚未配置，暂时无法登录。');
    if (busy.current) throw new Error('请等待当前操作完成。');
    busy.current = true;
    setAuthBusy(true);
    try {
      const email = values.email.trim().toLowerCase();
      if (kind === 'register') {
        const problem = passwordProblem(values.password);
        if (problem) throw new Error(problem);
        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password: values.password,
          options: {
            data: { display_name: values.displayName?.trim() ?? '' },
            emailRedirectTo: window.location.origin,
          },
        });
        if (signUpError) throw new Error(friendlyAuthError(signUpError));
        if (!data.session || !data.user)
          return { status: 'confirm-email' as const, user: null, mergedFavorites: 0 };
        const mergedFavorites = await mergeFavoritesQuietly(data.user.id);
        await refresh(true);
        broadcast('auth');
        return { status: 'signed-in' as const, user: accountUser(data.user), mergedFavorites };
      }
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password: values.password,
      });
      if (signInError) throw new Error(friendlyAuthError(signInError));
      const mergedFavorites = await mergeFavoritesQuietly(data.user.id);
      await refresh(true);
      broadcast('auth');
      setRecovery(false);
      return { status: 'signed-in' as const, user: accountUser(data.user), mergedFavorites };
    } finally {
      busy.current = false;
      setAuthBusy(false);
    }
  }

  async function logout() {
    if (!supabase || busy.current) return;
    busy.current = true;
    setAuthBusy(true);
    try {
      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) throw new Error(friendlyAuthError(signOutError));
      setRecovery(false);
      await refresh(true);
      broadcast('auth');
    } finally {
      busy.current = false;
      setAuthBusy(false);
    }
  }

  async function updateProfile(displayName: string) {
    if (!supabase || !current.current.user) throw new Error('请先登录账号。');
    const name = displayName.trim();
    if (!name || Array.from(name).length > 24) throw new Error('昵称需为 1–24 个字符。');
    setAuthBusy(true);
    try {
      const { error: profileError } = await supabase.auth.updateUser({
        data: { display_name: name },
      });
      if (profileError) throw new Error(friendlyAuthError(profileError));
      await supabase
        .from('profiles')
        .update({ display_name: name })
        .eq('id', current.current.user.id);
      await refresh(true);
    } finally {
      setAuthBusy(false);
    }
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    if (!supabase || !current.current.user) throw new Error('请先登录账号。');
    const problem = passwordProblem(newPassword);
    if (problem) throw new Error(problem);
    setAuthBusy(true);
    try {
      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email: current.current.user.email,
        password: currentPassword,
      });
      if (verifyError) throw new Error('当前密码不正确。');
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) throw new Error(friendlyAuthError(updateError));
      await supabase.auth.signOut({ scope: 'others' });
    } finally {
      setAuthBusy(false);
    }
  }

  async function verifyEmail(email?: string) {
    if (!supabase) throw new Error('Supabase 尚未配置，暂时无法发送邮件。');
    const target = (email ?? current.current.user?.email ?? '').trim().toLowerCase();
    if (!target) throw new Error('请先填写邮箱。');
    const { error: resendError } = await supabase.auth.resend({
      type: 'signup',
      email: target,
      options: { emailRedirectTo: window.location.origin },
    });
    if (resendError) throw new Error(friendlyAuthError(resendError));
  }

  async function requestReset(email: string) {
    if (!supabase) throw new Error('Supabase 尚未配置，暂时无法找回密码。');
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(
      email.trim().toLowerCase(),
      { redirectTo: window.location.origin },
    );
    if (resetError) throw new Error(friendlyAuthError(resetError));
  }

  async function updatePassword(newPassword: string) {
    if (!supabase) throw new Error('Supabase 尚未配置，暂时无法更新密码。');
    const problem = passwordProblem(newPassword);
    if (problem) throw new Error(problem);
    setAuthBusy(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) throw new Error(friendlyAuthError(updateError));
      setRecovery(false);
      await refresh(true);
    } finally {
      setAuthBusy(false);
    }
  }

  return {
    ...state,
    loading,
    error,
    authBusy,
    favoriteSyncing,
    recovery,
    loadCatalog: () => refresh(false),
    refreshQuiet: () => refresh(true),
    toggleFavorite,
    saveFavorite,
    authenticate,
    logout,
    requestReset,
    updatePassword,
    clearRecovery: () => setRecovery(false),
    updateProfile,
    changePassword,
    verifyEmail,
    currentUserId: () => current.current.user?.id ?? null,
  };
}
